import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AuthLayout } from '@/components/layout/auth-layout';
import { useAuthStore, type BillingBlockReason } from '@/lib/auth-store';

/**
 * Tela de bloqueio por inadimplencia — renderizada por
 * <RequireActiveSubscription> (lib/auth-guard.tsx) no lugar do app inteiro
 * quando `billingBlocked` e true, em qualquer rota que nao seja
 * /escritorio/configuracoes/assinatura.
 *
 * Mesmo padrao visual de sem-escritorio.tsx (AuthLayout + Card): estado
 * critico fora do fluxo normal do dashboard, nao uma tela de configuracoes.
 */

const REASON_TEXT: Record<BillingBlockReason, string> = {
  past_due: 'Seu último pagamento não foi processado.',
  suspended: 'Sua assinatura está suspensa.',
  canceled: 'Sua assinatura foi cancelada.',
};

function formatGraceDate(iso: string): string | null {
  try {
    return format(new Date(iso), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return null;
  }
}

export default function BillingBlockedPage() {
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const billingReason = useAuthStore((s) => s.billingReason);
  const graceEndsAt = useAuthStore((s) => s.graceEndsAt);

  const reasonText = billingReason
    ? REASON_TEXT[billingReason]
    : 'Não conseguimos confirmar sua assinatura.';

  const graceDate =
    billingReason === 'past_due' && graceEndsAt ? formatGraceDate(graceEndsAt) : null;

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Regularize sua assinatura para continuar</CardTitle>
          <CardDescription>{reasonText}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-[var(--fg-muted)]">
          {graceDate && (
            <p>
              Você tem até <strong className="text-[var(--fg)]">{graceDate}</strong> para
              regularizar antes que o acesso seja suspenso.
            </p>
          )}
          <p>
            Seus dados continuam seguros e intactos — assim que o pagamento for
            confirmado, o acesso volta automaticamente.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button
            type="button"
            className="w-full"
            onClick={() => navigate('/escritorio/configuracoes/assinatura')}
          >
            Ir para assinatura
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => void signOut('manual')}
          >
            Sair
          </Button>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
